import React from 'react';
import { TabType } from '../../../types/policyEditor';

/**
 * Props interface for the TabNavigation component
 * @property activeTab - Currently selected tab
 * @property setActiveTab - Callback function to change the active tab
 * @property selectedEffect - Currently selected policy effect
 * @property isPreviewVisible - Whether the preview section is currently visible
 * @property togglePreview - Callback function to toggle preview visibility
 */
interface TabNavigationProps {
  activeTab: TabType;
  setActiveTab: (tab: TabType) => void;
  selectedEffect: string;
  isPreviewVisible: boolean;
  togglePreview: () => void;
}

/**
 * TabNavigation component for managing policy editor tabs
 * 
 * This component provides navigation between different sections of the policy editor
 * as a compact vertical side panel with icons and labels:
 * - Basic Info: General policy information and settings
 * - Conditions: Policy condition configuration
 * - Parameters: Policy parameter management
 * - Details: Additional policy details (only visible for 'modify' effect)
 * - Alias Search: Search for Azure resource aliases
 * - Preview Toggle: Show/hide the preview section
 * - About: Information about the policy editor
 * 
 * The component dynamically shows/hides the Details tab based on the selected effect.
 * By default, only icons are shown. On hover, the panel expands to show text labels.
 * 
 * @example
 * <TabNavigation
 *   activeTab="basicInfo"
 *   setActiveTab={handleTabChange}
 *   selectedEffect="modify"
 *   isPreviewVisible={true}
 *   togglePreview={handleTogglePreview}
 * />
 */
export const TabNavigation: React.FC<TabNavigationProps> = ({
  activeTab,
  setActiveTab,
  selectedEffect,
  isPreviewVisible,
  togglePreview
}) => {
  return (
    <nav
      role="tablist"
      aria-label="Policy Editor Tabs"
      className="group flex flex-col gap-0.5 p-0.5 border-r border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900 !w-[40px] hover:!w-[180px] !min-w-[40px] hover:!min-w-[180px] !max-w-[40px] hover:!max-w-[180px] transition-all duration-300 ease-in-out overflow-hidden"
    >
      {/* Basic Info tab */}
      <button
        role="tab"
        aria-selected={activeTab === 'basicInfo'}
        className={`flex items-center gap-2 px-2 py-1.5 text-xs font-medium transition-colors duration-200 rounded whitespace-nowrap ${
          activeTab === 'basicInfo'
            ? 'bg-blue-100 text-blue-700 dark:bg-blue-900 dark:text-blue-300'
            : 'text-gray-600 hover:bg-gray-100 hover:text-gray-900 dark:text-gray-400 dark:hover:bg-gray-800 dark:hover:text-gray-200'
        } focus:outline-none focus-visible:ring-1 focus-visible:ring-blue-500`}
        onClick={() => setActiveTab('basicInfo')}
        title="Basic Info"
      >
        <svg className="w-5 h-5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
        <span className="opacity-0 group-hover:opacity-100 transition-opacity duration-300 text-xs leading-none ml-2">Basic Info</span>
      </button>

      {/* Conditions tab */}
      <button
        role="tab"
        aria-selected={activeTab === 'conditions'}
        className={`flex items-center gap-2 px-2 py-1.5 text-xs font-medium transition-colors duration-200 rounded whitespace-nowrap ${
          activeTab === 'conditions'
            ? 'bg-blue-100 text-blue-700 dark:bg-blue-900 dark:text-blue-300'
            : 'text-gray-600 hover:bg-gray-100 hover:text-gray-900 dark:text-gray-400 dark:hover:bg-gray-800 dark:hover:text-gray-200'
        } focus:outline-none focus-visible:ring-1 focus-visible:ring-blue-500`}
        onClick={() => setActiveTab('conditions')}
        title="Conditions"
      >
        <svg className="w-5 h-5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
        </svg>
        <span className="opacity-0 group-hover:opacity-100 transition-opacity duration-300 text-xs leading-none ml-2">Conditions</span>
      </button>

      {/* Parameters tab */}
      <button
        role="tab"
        aria-selected={activeTab === 'parameters'}
        className={`flex items-center gap-2 px-2 py-1.5 text-xs font-medium transition-colors duration-200 rounded whitespace-nowrap ${
          activeTab === 'parameters'
            ? 'bg-blue-100 text-blue-700 dark:bg-blue-900 dark:text-blue-300'
            : 'text-gray-600 hover:bg-gray-100 hover:text-gray-900 dark:text-gray-400 dark:hover:bg-gray-800 dark:hover:text-gray-200'
        } focus:outline-none focus-visible:ring-1 focus-visible:ring-blue-500`}
        onClick={() => setActiveTab('parameters')}
        title="Parameters"
      >
        <svg className="w-5 h-5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6V4m0 2a2 2 0 100 4m0-4a2 2 0 110 4m-6 8a2 2 0 100-4m0 4a2 2 0 100 4m0-4v2m0-6V4m6 6v10m6-2a2 2 0 100-4m0 4a2 2 0 100 4m0-4v2m0-6V4" />
        </svg>
        <span className="opacity-0 group-hover:opacity-100 transition-opacity duration-300 text-xs leading-none ml-2">Parameters</span>
      </button>

      {/* Details tab - shown for modify, auditIfNotExists, and deployIfNotExists effects */}
      {(selectedEffect.toLowerCase() === 'modify' || selectedEffect.toLowerCase() === 'auditifnotexists' || selectedEffect.toLowerCase() === 'deployifnotexists') && (
        <button
          role="tab"
          aria-selected={activeTab === 'details'}
          className={`flex items-center gap-2 px-2 py-1.5 text-xs font-medium transition-colors duration-200 rounded whitespace-nowrap ${
            activeTab === 'details'
              ? 'bg-blue-100 text-blue-700 dark:bg-blue-900 dark:text-blue-300'
              : 'text-gray-600 hover:bg-gray-100 hover:text-gray-900 dark:text-gray-400 dark:hover:bg-gray-800 dark:hover:text-gray-200'
          } focus:outline-none focus-visible:ring-1 focus-visible:ring-blue-500`}
          onClick={() => setActiveTab('details')}
          title="Details"
        >
          <svg className="w-5 h-5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
          </svg>
          <span className="opacity-0 group-hover:opacity-100 transition-opacity duration-300 text-xs leading-none ml-2">Details</span>
        </button>
      )}

      {/* Existence Condition tab - shown only for auditIfNotExists and deployIfNotExists effects */}
      {(selectedEffect.toLowerCase() === 'auditifnotexists' || selectedEffect.toLowerCase() === 'deployifnotexists') && (
        <button
          role="tab"
          aria-selected={activeTab === 'existenceCondition'}
          className={`flex items-center gap-2 px-2 py-1.5 text-xs font-medium transition-colors duration-200 rounded whitespace-nowrap ${
            activeTab === 'existenceCondition'
              ? 'bg-blue-100 text-blue-700 dark:bg-blue-900 dark:text-blue-300'
              : 'text-gray-600 hover:bg-gray-100 hover:text-gray-900 dark:text-gray-400 dark:hover:bg-gray-800 dark:hover:text-gray-200'
          } focus:outline-none focus-visible:ring-1 focus-visible:ring-blue-500`}
          onClick={() => setActiveTab('existenceCondition')}
          title="Existence Condition"
        >
          <svg className="w-5 h-5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
          </svg>
          <span className="opacity-0 group-hover:opacity-100 transition-opacity duration-300 text-xs leading-none ml-2">Existence Condition</span>
        </button>
      )}

      {/* Deployment tab - shown only for deployIfNotExists effect */}
      {selectedEffect.toLowerCase() === 'deployifnotexists' && (
        <button
          role="tab"
          aria-selected={activeTab === 'deployment'}
          className={`flex items-center gap-2 px-2 py-1.5 text-xs font-medium transition-colors duration-200 rounded whitespace-nowrap ${
            activeTab === 'deployment'
              ? 'bg-blue-100 text-blue-700 dark:bg-blue-900 dark:text-blue-300'
              : 'text-gray-600 hover:bg-gray-100 hover:text-gray-900 dark:text-gray-400 dark:hover:bg-gray-800 dark:hover:text-gray-200'
          } focus:outline-none focus-visible:ring-1 focus-visible:ring-blue-500`}
          onClick={() => setActiveTab('deployment')}
          title="Deployment"
        >
          <svg className="w-5 h-5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
          </svg>
          <span className="opacity-0 group-hover:opacity-100 transition-opacity duration-300 text-xs leading-none ml-2">Deployment</span>
        </button>
      )}

      {/* Divider between main policy tabs and utility tabs */}
      <div className="my-2 mx-2 border-t border-gray-300 dark:border-gray-600"></div>

      {/* Alias Search tab */}
      <button
        role="tab"
        aria-selected={activeTab === 'aliasSearch'}
        className={`flex items-center gap-2 px-2 py-1.5 text-xs font-medium transition-colors duration-200 rounded whitespace-nowrap ${
          activeTab === 'aliasSearch'
            ? 'bg-blue-100 text-blue-700 dark:bg-blue-900 dark:text-blue-300'
            : 'text-gray-600 hover:bg-gray-100 hover:text-gray-900 dark:text-gray-400 dark:hover:bg-gray-800 dark:hover:text-gray-200'
        } focus:outline-none focus-visible:ring-1 focus-visible:ring-blue-500`}
        onClick={() => setActiveTab('aliasSearch')}
        title="Alias Search"
      >
        <svg className="w-5 h-5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
        </svg>
        <span className="opacity-0 group-hover:opacity-100 transition-opacity duration-300 text-xs leading-none ml-2">Aliases</span>
      </button>

      {/* Import tab */}
      <button
        role="tab"
        aria-selected={activeTab === 'import'}
        className={`flex items-center gap-2 px-2 py-1.5 text-xs font-medium transition-colors duration-200 rounded whitespace-nowrap ${
          activeTab === 'import'
            ? 'bg-blue-100 text-blue-700 dark:bg-blue-900 dark:text-blue-300'
            : 'text-gray-600 hover:bg-gray-100 hover:text-gray-900 dark:text-gray-400 dark:hover:bg-gray-800 dark:hover:text-gray-200'
        } focus:outline-none focus-visible:ring-1 focus-visible:ring-blue-500`}
        onClick={() => setActiveTab('import')}
        title="Import Policy"
      >
        <svg className="w-5 h-5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M9 19l3 3m0 0l3-3m-3 3V10" />
        </svg>
        <span className="opacity-0 group-hover:opacity-100 transition-opacity duration-300 text-xs leading-none ml-2">Import</span>
      </button>

      {/* Preview Toggle */}
      <button
        className={`flex items-center gap-2 px-2 py-1.5 text-xs font-medium transition-colors duration-200 rounded whitespace-nowrap ${
          isPreviewVisible
            ? 'text-blue-600 hover:bg-gray-100 hover:text-blue-700 dark:text-blue-400 dark:hover:bg-gray-800 dark:hover:text-blue-300'
            : 'text-gray-600 hover:bg-gray-100 hover:text-gray-900 dark:text-gray-400 dark:hover:bg-gray-800 dark:hover:text-gray-200'
        } focus:outline-none focus-visible:ring-1 focus-visible:ring-blue-500`}
        onClick={togglePreview}
        title={isPreviewVisible ? "Hide Preview" : "Show Preview"}
      >
        <svg className="w-5 h-5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
        </svg>
        <span className="opacity-0 group-hover:opacity-100 transition-opacity duration-300 text-xs leading-none ml-2">
          {isPreviewVisible ? "Hide Preview" : "Show Preview"}
        </span>
      </button>

      {/* About tab */}
      <button
        role="tab"
        aria-selected={activeTab === 'about'}
        className={`flex items-center gap-2 px-2 py-1.5 text-xs font-medium transition-colors duration-200 rounded whitespace-nowrap ${
          activeTab === 'about'
            ? 'bg-blue-100 text-blue-700 dark:bg-blue-900 dark:text-blue-300'
            : 'text-gray-600 hover:bg-gray-100 hover:text-gray-900 dark:text-gray-400 dark:hover:bg-gray-800 dark:hover:text-gray-200'
        } focus:outline-none focus-visible:ring-1 focus-visible:ring-blue-500`}
        onClick={() => setActiveTab('about')}
        title="About"
      >
        <svg className="w-5 h-5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
        <span className="opacity-0 group-hover:opacity-100 transition-opacity duration-300 text-xs leading-none ml-2">About</span>
      </button>
    </nav>
  );
}; 